import { roleLabel } from '@nolens/domain';
import { Banner, Button, EmptyState, ErrorState, Logo, Shot, useLocale } from '@nolens/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useRouteError, useSearchParams } from 'react-router';
import { GuideSteps } from '../components/GuideSteps';
import { QueryView } from '../components/Query';
import { api, ApiError, publicBrandUrl, publicImageUrl } from '../lib/api';
import { brandStyle, usePublicFont } from '../lib/brand';
import { detectExtension, linkExtension } from '../lib/extension';
import { useInvitePreview, usePublicGuide } from '../lib/queries';
import { WithMe } from './workspace';
import { useQueryClient } from '@tanstack/react-query';
import { miscText } from '../i18n/misc';
import { shellText } from '../i18n/shell';
import { reportError } from '../lib/errors';
import { privacyText, type LegalText } from '../i18n/privacy';
import { termsText } from '../i18n/terms';
import { refundsText } from '../i18n/refunds';
import { LOCALE_NAMES, type Locale } from '@nolens/i18n';
import { publicPaths } from '../i18n/landing';

export function InvitePage() {
  const { token } = useParams();
  const locale = useLocale();
  const t = miscText(locale);
  const preview = useInvitePreview(token);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accept = useMutation({
    mutationFn: () =>
      api<{ workspaceId: string }>(`/v1/invites/${token}/accept`, { method: 'POST' }),
    onSuccess: ({ workspaceId }) => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      navigate(`/w/${workspaceId}`, { replace: true });
    },
  });
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-5 py-10">
      <Logo size={28} />
      <QueryView q={preview}>
        {(p) => (
          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {t.inviteTo(p.workspaceName)}
              </h1>
              <p className="mt-2 text-[15px] text-text-2">
                {t.inviteHint(roleLabel(p.role, locale))}
              </p>
            </div>
            {accept.error instanceof ApiError && (
              <Banner tone="critical">{accept.error.message}</Banner>
            )}
            <Button size="lg" loading={accept.isPending} onClick={() => accept.mutate()}>
              {t.acceptInvite}
            </Button>
          </div>
        )}
      </QueryView>
    </main>
  );
}

/** Página pública de um guia partilhado por ligação (e o embed): sem conta, só leitura, com a marca do espaço. */
export function PublicGuidePage({ embed = false }: { embed?: boolean }) {
  const { token } = useParams();
  const t = miscText(useLocale());
  const guide = usePublicGuide(token);
  const b = guide.data?.brand ?? null;
  const fontReady = usePublicFont(
    b?.font === 'custom' && b.fontVersion && token
      ? publicBrandUrl(token, 'font', b.fontVersion)
      : null,
  );
  // Uma leitura por sessão do browser.
  useEffect(() => {
    if (!token) return;
    const key = 'viewed:' + token;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // sem armazenamento: conta na mesma
    }
    void api(`/public/guides/${token}/view${embed ? '?src=embed' : ''}`, {
      method: 'POST',
      anonymous: true,
    }).catch(() => undefined);
  }, [token, embed]);
  const logo = b?.logoVersion && token ? publicBrandUrl(token, 'logo', b.logoVersion) : null;
  return (
    <div className="min-h-dvh" style={brandStyle(b, fontReady)}>
      {!embed && (
        <header className="no-print border-b border-border">
          <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
            {logo ? (
              <img
                src={logo}
                alt={guide.data?.workspaceName ?? ''}
                className="h-8 w-auto max-w-[200px]"
                data-brand-logo
              />
            ) : (
              <a href="/" aria-label="Nolens Guides">
                <Logo />
              </a>
            )}
          </div>
        </header>
      )}
      <main className={embed ? 'px-4 py-5' : 'mx-auto max-w-3xl px-4 py-8'}>
        <QueryView q={guide}>
          {(g) =>
            embed && !g.embed ? (
              <p className="text-[15px] text-text-2" data-embed-off>
                {t.embedOff}{' '}
                <a className="underline" href={'/s/' + token} target="_blank" rel="noreferrer">
                  {t.openGuide}
                </a>
              </p>
            ) : (
              <article>
                {!embed && <p className="text-sm text-text-3">{g.workspaceName}</p>}
                <h1
                  className={
                    embed
                      ? 'text-[22px] font-semibold tracking-tight'
                      : 'mt-1 text-[28px] font-semibold tracking-tight'
                  }
                >
                  {g.title || t.untitled}
                </h1>
                {g.description && (
                  <p className="mt-2 whitespace-pre-wrap text-[16px] leading-relaxed text-text-2">
                    {g.description}
                  </p>
                )}
                <div className="mt-6">
                  <GuideSteps
                    steps={g.steps}
                    locale={g.language}
                    shot={(s, zoom) => (
                      <Shot
                        step={s}
                        zoom={zoom}
                        src={s.imageId ? publicImageUrl(token!, s.imageId) : null}
                      />
                    )}
                  />
                </div>
                <p className="no-print mt-10 text-center text-[13px] text-text-3">
                  {b?.footer.trim() ? (
                    b.footer
                  ) : (
                    <>
                      {t.madeWith}{' '}
                      <a
                        className="underline"
                        href={embed ? window.location.origin : '/'}
                        target={embed ? '_blank' : undefined}
                        rel="noreferrer"
                      >
                        Nolens Guides
                      </a>
                    </>
                  )}
                </p>
              </article>
            )
          }
        </QueryView>
      </main>
    </div>
  );
}

/** Ligar a extensão a esta conta. A extensão recebe uma sessão sua (POST /v1/extension/link). */
export function ExtensionPage() {
  const [params] = useSearchParams();
  const t = miscText(useLocale());
  const st = shellText(useLocale());
  const qc = useQueryClient();
  const ext = useQuery({
    queryKey: ['extension'],
    queryFn: () => detectExtension(),
    staleTime: 0,
    gcTime: 0,
    // Sem extensão, continua a procurar: quem a instala vê-a aparecer aqui, sem recarregar
    refetchInterval: (q) => (q.state.data ? false : 2000),
  });
  const link = useMutation({
    mutationFn: async () => {
      const r = await linkExtension();
      if (!r.ok) throw new Error(r.error ?? t.refused);
      return r.email;
    },
    onSuccess: () => {
      void ext.refetch();
      // Os primeiros passos (barra lateral, Início) passam logo a «ligada»
      void qc.invalidateQueries({ queryKey: ['extension', 'shell'] });
    },
  });
  // Vindo do painel da extensão («Ligar conta»): liga sem mais cliques.
  const auto = useRef(false);
  useEffect(() => {
    if (params.get('ligar') === '1' && ext.data && !auto.current) {
      auto.current = true;
      link.mutate();
    }
  }, [params, ext.data, link]);

  return (
    <WithMe crumbs={() => [{ label: st.extension }]}>
      {(me) => (
        <section className="max-w-xl">
          <h1 className="text-2xl font-semibold tracking-tight">{t.extensionTitle}</h1>
          <p className="mt-2 text-[15px] text-text-2">{t.extensionHint}</p>
          <div className="mt-6">
            {ext.isPending ? (
              <p className="text-text-2">{t.lookingForExtension}</p>
            ) : !ext.data ? (
              <EmptyState
                title={t.extensionNotFound}
                description={me.extensionUrl ? t.extensionNotFoundStore : t.extensionNotFoundHint}
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    {me.extensionUrl && (
                      <a href={me.extensionUrl} target="_blank" rel="noopener noreferrer">
                        <Button data-install-extension>{t.installExtension}</Button>
                      </a>
                    )}
                    <Button
                      variant={me.extensionUrl ? 'ghost' : 'secondary'}
                      onClick={() => void ext.refetch()}
                    >
                      {t.searchAgain}
                    </Button>
                  </div>
                }
              />
            ) : (
              <>
                <div className="flex flex-col gap-4 rounded-[14px] bg-surface-1 p-5 shadow-[inset_0_0_0_1px_var(--border)]">
                  <p>
                    {t.installed(ext.data.version)}{' '}
                    {ext.data.linkedEmail ? (
                      <>
                        {t.linkedTo} <strong>{ext.data.linkedEmail}</strong>.
                      </>
                    ) : (
                      t.notLinked
                    )}
                  </p>
                  {ext.data.linkedEmail !== me.user.email && (
                    <Button loading={link.isPending} onClick={() => link.mutate()}>
                      {t.connectTo(me.user.email ?? t.thisAccount)}
                    </Button>
                  )}
                  {link.isSuccess && <Banner tone="positive">{t.linked}</Banner>}
                  {link.error && <Banner tone="critical">{link.error.message}</Banner>}
                </div>
                <div className="mt-6" data-first-guide>
                  <h2 className="text-[15px] font-semibold">{t.firstGuideTitle}</h2>
                  <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[15px] text-text-2">
                    <li>{t.firstGuidePin}</li>
                    <li>{t.firstGuideRecord}</li>
                    <li>{t.firstGuideFinish}</li>
                  </ol>
                  <p className="mt-4 text-sm leading-relaxed text-text-3" data-history-note>
                    {t.historyNote}
                  </p>
                </div>
              </>
            )}
          </div>
          <p className="mt-6 text-sm text-text-3">
            <Link to="/inicio" className="underline">
              {t.backToGuides}
            </Link>
          </p>
        </section>
      )}
    </WithMe>
  );
}

/** Página de um texto legal (privacidade, termos, reembolsos), na língua da interface. */
function LegalPage({
  text,
  page,
}: {
  text: (l: Locale) => LegalText;
  page: 'privacy' | 'terms' | 'refunds';
}) {
  const locale = useLocale();
  const t = text(locale);
  // A mesma página na outra língua (cada língua tem o seu endereço)
  const other: Locale = locale === 'en' ? 'pt-PT' : 'en';
  return (
    <main className="mx-auto max-w-2xl px-5 py-10 leading-relaxed">
      <div className="flex items-center justify-between gap-4">
        <Link to="/" aria-label="Nolens Guides">
          <Logo size={28} />
        </Link>
        <Link
          to={publicPaths(other)[page]}
          lang={other}
          className="text-sm text-text-2 underline-offset-2 hover:text-text-1 hover:underline"
          data-other-language
        >
          {LOCALE_NAMES[other]}
        </Link>
      </div>
      <h1 className="mt-8 text-2xl font-semibold tracking-tight">{t.title}</h1>
      <p className="mt-4 text-text-2">{t.updated}</p>
      {t.sections.map((s) => (
        <section key={s.h}>
          <h2 className="mt-8 text-lg font-semibold">{s.h}</h2>
          {s.p && <p className="mt-2 text-text-2">{s.p}</p>}
          {s.li && (
            <ul className="mt-2 list-disc pl-5 text-text-2">
              {s.li.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </main>
  );
}

export const PrivacyPage = () => <LegalPage text={privacyText} page="privacy" />;
export const TermsPage = () => <LegalPage text={termsText} page="terms" />;
export const RefundsPage = () => <LegalPage text={refundsText} page="refunds" />;

/** Erro ao mostrar uma página (o router apanha-o): regista e deixa tentar outra vez. */
export function RouteError() {
  const err = useRouteError();
  useEffect(() => reportError(err), [err]);
  return <ErrorState onRetry={() => window.location.reload()} />;
}

export function NotFoundPage() {
  const t = miscText(useLocale());
  return (
    <EmptyState
      title={t.pageNotFound}
      description={t.pageNotFoundHint}
      action={
        <Link to="/">
          <Button variant="secondary">{t.goToGuides}</Button>
        </Link>
      }
    />
  );
}
